import React, { useState, useContext } from 'react';
import { LogIn, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import axios from '../axios-config'; // Use configured axios with interceptors

// Moved login functionality from app.tsx into this file
const login = async (Username: string, Password: string) => {
  const { data } = await axios.post('/api/account/login', {
    email: Username,
    password: Password
  });
  if (!data.success) {
    throw new Error('Invalid username or password');
  }
  return data;
};

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { fetchUserProfile } = useContext(UserContext);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
  try { localStorage.setItem('acorn:preferredAuth', 'local'); } catch {}
      const result = await login(username, password);
      if (result.success) {
        await fetchUserProfile();
        navigate('/');
      } else {
        setError('Invalid username or password');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Invalid username or password');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center p-4">
      {/* Logo */}
      <img
        src="https://i.imgur.com/UE5wDds.png"
        alt="ClassDojo Logo"
        className="w-24 h-24 mb-8"
      />
      {process.env.NODE_ENV !== 'PROD' && process.env.NODE_ENV && (
        <span
          className={`text-4xl ml-2 ${process.env.NODE_ENV === "demo"
            ? "text-blue-600"
            : process.env.NODE_ENV === "test"
              ? "text-orange-600"
              : process.env.NODE_ENV === "development"
                ? "text-purple-600"
                : "text-blue-600"
            }`}
          style={{ zIndex: 9999, opacity: 0.5 }}
        >
          ({process.env.NODE_ENV.toUpperCase()})
          <br />
        </span>
      )}
      <div className="bg-white/60 backdrop-blur-md p-8 rounded-2xl shadow-sm border border-gray-200/50 max-w-md w-full">
        <h1 className="text-2xl font-bold text-center text-gray-900 mb-8">
          Log in to your account
        </h1>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
              Username
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#673AB7] focus:border-transparent transition-colors"
              placeholder="Enter your username"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#673AB7] focus:border-transparent transition-colors"
              placeholder="Enter your password"
              required
            />
          </div>

          {error && (
            <div className="text-red-500 text-sm text-center">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-gray-800/80 text-white py-2 px-4 rounded-lg hover:bg-gray-900/80 transition-all backdrop-blur-sm flex items-center justify-center gap-2"
          >
            <LogIn className="w-5 h-5" />
            Log In
          </button>
        </form>

        {/* Smaller Google school login button (same behavior as App.tsx) */}
        <div className="mt-6 flex flex-col items-center gap-3">
          <button
            onClick={() => {
              try { localStorage.setItem('acorn:preferredAuth', 'google'); } catch {}
              window.location.href = `${process.env.API_URL || process.env.VITE_API_URL}/api/account/signingoogle?returnUrl=${encodeURIComponent(window.location.origin)}`;
            }}
            className="flex items-center gap-3 px-4 py-2 rounded-lg bg-white/80 border border-gray-200/50 hover:shadow-sm transition-all"
            aria-label="Sign in with Google"
          >
            <div className="w-8 h-8 bg-white rounded-md flex items-center justify-center border border-gray-200/50">
              <img
                src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_272x92dp.png"
                alt="Google"
                className="w-5 h-auto"
              />
            </div>
            <span className="text-sm text-gray-900 font-medium">Sign in with Google</span>
          </button>

          <div className="text-center">
            <Link to="/" className="text-gray-600 hover:text-gray-800 text-sm inline-flex items-center">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to login options
            </Link>
          </div>
          {(process.env.NODE_ENV === "demo" || process.env.NODE_ENV === "development") && (
            <div className="text-center mt-3">
              <Link to="/signup" className="text-sm text-blue-600 hover:underline">
                Don't have an account? Sign up (demo)
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}